import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { forgotPassword, resetPasswordWithOtp } from "../api/loginApis";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !isValidEmail) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await forgotPassword({ email });
      toast.success(response.message || "OTP sent to your email!");
      localStorage.setItem("resetToken", response.resetToken);
      setStep(2);
    } catch (err) {
      let errorMessage = "Failed to send OTP. Please try again.";

      if (err.response) {
        errorMessage = err.response.data?.message || err.response.statusText || errorMessage;
      } else if (err.request) {
        errorMessage = "No response from server. Check your network.";
      } else {
        errorMessage = err.message || errorMessage;
      }

      console.error("Forgot Password Error:", err);
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp || otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const resetToken = localStorage.getItem("resetToken");
      if (!resetToken) {
        throw new Error("Reset token missing. Please request OTP again.");
      }

      const response = await resetPasswordWithOtp({
        otp,
        newPassword,
        resetToken,
      });

      toast.success(response.message || "Password reset successful!");
      localStorage.removeItem("resetToken");
      navigate("/");
    } catch (err) {
      let errorMessage = "Failed to reset password.";

      if (err.response) {
        errorMessage = err.response.data?.message || errorMessage;
      } else if (err.request) {
        errorMessage = "No response from server.";
      } else {
        errorMessage = err.message || errorMessage;
      }

      console.error("Reset Password Error:", err);
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {step === 1 ? "Forgot Password" : "Reset Your Password"}
        </h2>

        {step === 1 ? (
          <>
            <p className="text-center text-gray-600 mb-8">
              Enter your Registered email address and we'll send you a 4-digit OTP to reset your password.
            </p>

            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your Registered Email Id"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                  }`}
                  required
                  disabled={loading}
                />
                {error && <p className="text-red-600 text-sm mt-1">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={loading || !isValidEmail}
                className={`w-full py-3 font-medium rounded-lg transition ${
                  loading || !isValidEmail
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                }`}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="text-center text-gray-600 mb-8">
              Enter the 4-digit OTP sent to <strong>{email}</strong> and your new password.
            </p>

            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* OTP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OTP (4 digits)
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="Enter 4-digit code"
                  maxLength={4}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition pr-10 border-gray-300 focus:ring-gray-700 ${
                    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                  }`}
                  required
                  disabled={loading}
                />
              </div>

              {/* New Password with Eye Icon */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition pr-10 ${
                    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                  }`}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Confirm Password with Eye Icon */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition pr-10 ${
                    error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-gray-700"
                  }`}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-10 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {error && <p className="text-red-600 text-sm mt-1 text-center">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 bg-black text-white font-medium rounded-lg transition ${
                  loading ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-800"
                }`}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>

              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-gray-600 hover:text-black text-sm underline"
                >
                  Didn't receive OTP? Resend
                </button>
              </div>
            </form>
          </>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-gray-600 hover:text-black text-sm"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;